/** English catalog keys for breeding values, shared by contract fields, facts and screens. */

export const STATUS_LABELS = {
  open: "Open (not pregnant)",
  exposed: "With a bull",
  bred: "Bred, not checked",
  pregnant: "Pregnant",
  calved: "Calved, not bred yet",
  unknown: "No breeding records",
} as const;

/** Short status words for chips and tiles. */
export const SHORT_STATUS = {
  pregnant: "Pregnant", bred: "Bred", exposed: "With bull", open: "Not pregnant", calved: "Calved", unknown: "No records",
} as const;

/** Fetal age units as people say them. */
export const AGE_UNIT_LABELS = { d: "Days", wk: "Weeks", mo: "Months" } as const;

export const SIGNS = {
  standing: "Standing to be mounted",
  mounting: "Mounting others",
  mucus: "Clear mucus",
  restless: "Restless or bawling",
  patch: "Heat patch or chalk rubbed",
  other: "Other sign",
} as const;

export const HEAT_METHODS = { visual: "Seen", patch: "Heat patch or tail chalk", monitor: "Activity monitor", marker_bull: "Marker bull" } as const;
export const HEAT_SCALES = { "1_5": "1 to 5", "1_100": "1 to 100", other: "Other scale" } as const;

export const AI_KINDS = { timed: "Timed AI", heat: "On a heat", repeat: "Repeat breeding" } as const;
export const AI_RESULTS = { bred: "Bred", failed: "Couldn't finish" } as const;

export const EXPOSURE_ACTIONS = { in: "Bull in", out: "Bull out" } as const;

export const CHECK_RESULTS = { pregnant: "Pregnant", open: "Checked open", recheck: "Recheck" } as const;
export const CHECK_METHODS = { palpation: "Palpation", ultrasound: "Ultrasound", blood: "Blood test", other: "Other" } as const;
export const STAGES = { early: "Early (short bred)", middle: "Middle", late: "Late (heavy bred)" } as const;
export const FETAL_SEX = { female: "Heifer", male: "Bull" } as const;

export const EASE = {
  no_help: "No help",
  easy_pull: "Easy pull",
  hard_pull: "Hard pull",
  c_section: "C-section",
  malpresentation: "Calf turned wrong",
} as const;
export const ASSISTED = new Set(["easy_pull", "hard_pull", "c_section", "malpresentation"]);

export const PRESENTATION = { normal: "Front feet first", backward: "Backward", breech: "Breech", other: "Other" } as const;
export const OBSERVED = { seen: "Seen calving", found: "Found with the calf" } as const;
export const CALF_SEX = { female: "Heifer", male: "Bull", unknown: "Not known" } as const;
export const CALF_OUTCOME = { alive: "Alive", born_dead: "Born dead" } as const;
export const WEIGHT_BASIS = { measured: "Weighed", estimated: "Estimated" } as const;
export const SIRE_BASIS = {
  confirmed: "Confirmed (AI date, ET or DNA)",
  likely: "Likely, from breeding records",
  unknown: "Not known",
} as const;

export const LOSS_KINDS = { abortion: "Aborted", early_loss: "Lost the pregnancy early", unknown: "Not sure" } as const;

export const FOSTER_REASONS = {
  dam_died: "Dam died",
  no_milk: "Dam has no milk",
  rejected: "Dam won't take the calf",
  twin: "Twin",
  nurse_lost_calf: "Nurse cow lost her calf",
  other: "Other",
} as const;
export const FOSTER_METHODS = { graft: "Grafted (skin or scent)", penned: "Penned together", adopted: "She took it on her own", other: "Other" } as const;
export const FOSTER_RESULTS = { took: "Took", trying: "Still working on it", failed: "Didn't take" } as const;

export const EMBRYO_STATES = { fresh: "Fresh", frozen: "Frozen-thawed" } as const;
export const TRANSFER_RESULTS = { transferred: "Transferred", not_transferred: "Thawed but not transferred" } as const;

export const DECISIONS = {
  keep: "Keep for breeding",
  defer: "Decide later",
  cull: "Cull",
  sell_open: "Sell open",
  do_not_breed: "Don't breed",
} as const;
export const USES = { any: "Any breeding", natural: "Natural service", ai: "AI", donor: "Embryo donor", recipient: "Embryo recipient" } as const;
export const REASONS = {
  open: "Open at preg check",
  late: "Calved late",
  age: "Age",
  feet_legs: "Feet and legs",
  udder: "Udder or teats",
  temperament: "Temperament",
  calving_trouble: "Calving trouble",
  poor_calf: "Poor calf",
  exam: "Breeding soundness exam",
  health: "Health",
  good_record: "Good record",
  other: "Other",
} as const;
export const BSE_RESULTS = {
  satisfactory: "Satisfactory",
  unsatisfactory: "Unsatisfactory",
  deferred: "Deferred",
  not_examined: "Not examined",
} as const;

export const BASIS_LABELS = {
  fetal_age: "From fetal age at the check",
  service: "From the breeding date",
  transfer: "From the transfer date",
  exposure: "From when the bulls were in",
} as const;
